import {
  Banner,
  Box,
  EmptyState,
  FlexBox,
  Icon,
  List,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import type { Instrument, InstrumentPanel } from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import snakeCase from 'lodash/snakeCase';
import { useMemo } from 'react';
import { useCSVDownloader } from 'react-papaparse';


import { useAnalyticsState } from '../../utils/useAnalyticsState/useAnalyticsState';

import styles from './analyticsExport.module.scss';
import { instrumentAssets } from './utils/instrumentAssets';
import { transformInstrumentsToCSV } from './utils/transformInstrumentsToCSV';

type AnalyticsExportProps = {
  panel: InstrumentPanel;
  instruments: readonly Instrument[];
};

export const AnalyticsExport = ({
  panel,
  instruments,
}: AnalyticsExportProps): JSX.Element => {
  const { dashboard, settings } = useAnalyticsState();
  const { CSVDownloader, Type: CSVDownloaderType } = useCSVDownloader();

  const csvRecords = useMemo(
    () => transformInstrumentsToCSV(instruments, settings),
    [instruments, settings],
  );

  if (!csvRecords.length) {
    return (
      <FlexBox
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <EmptyState
          title="Nothing to export"
          description="There is no data available for the selected period and filters."
          icon="download"
        />
      </FlexBox>
    );
  }

  return (
    <FlexBox flexDirection="column" gap="300">
      <Banner
        icon="download"
        variant="success"
        headline={
          csvRecords.length === 1
            ? 'Your CSV file is ready'
            : 'Your CSV files are ready'
        }
        description={
          <Text.Paragraph variant="4">
            {csvRecords.length === 1
              ? 'Click the link below to download the CSV file to your computer.'
              : 'Click each of the links below to download the CSV files to your computer.'}
          </Text.Paragraph>
        }
      />

      <Box borderColor="neutral.200" borderWidth="100" borderRadius="100">
        <List className={styles.list}>
          {csvRecords.map(({ instrumentType, data, meta }, index) => {
            const fileName = `${snakeCase(
              `${panel.title} ${csvRecords.length > 1 ? `${index + 1}` : ''}`,
            )}`;

            const { icon, title } = instrumentAssets[instrumentType];

            return (
              <List.Item
                className={styles.listItem}
                key={index}
                title={fileName}
                variant="large"
                contentLeft={
                  <Tooltip
                    content={title}
                    wrap="div"
                    wrapProps={{ className: styles.iconWrap }}
                    dark
                  >
                    <Icon name={icon} />
                  </Tooltip>
                }
                contentRight={
                  <FlexBox alignSelf="flex-end" alignItems="center" gap="250">
                    {meta?.warning && (
                      <Tooltip
                        className={styles.tooltip}
                        content={meta.warning}
                        dark
                      >
                        <Icon name="exclamation" color={colors.orange[500]} />
                      </Tooltip>
                    )}

                    <Text.Paragraph variant="2">
                      <CSVDownloader
                        className={styles.link}
                        type={CSVDownloaderType.Link}
                        filename={fileName}
                        bom={true}
                        config={{
                          delimiter: ',',
                        }}
                        data={() => {
                          // React-papaparse will only call the data callback
                          // when clicking the link, so we can use this to track
                          // when the user actually downloads the CSV file.
                          ampli.analyticsExportDownloadClicked({
                            dashboard: dashboard.title,
                            panel: panel.title,
                            instrumentType,
                            fileType: 'csv',
                          });

                          return data;
                        }}
                      >
                        Download
                      </CSVDownloader>
                    </Text.Paragraph>
                  </FlexBox>
                }
              />
            );
          })}
        </List>
      </Box>
    </FlexBox>
  );
};
